import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Space, Tag, Modal, Form, Select, Input, message, Drawer } from 'antd';
import { PlusOutlined, EyeOutlined, CheckCircleOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { listInventaires, createInventaire, deleteInventaire, validateInventaire, recalcStockFromInventaire } from '../../models/inventaireAPI';
import { useFetchAllEntrepots, useDefaultEntrepot } from '../../hooks/entrepotHooks';
import { getDefaultEntrepot } from '../../utils/defaultEntrepot';

const { Option } = Select;
const { TextArea } = Input;

const InventaireList = ({ onSelectInventaire }) => {
  const [inventaires, setInventaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [filters, setFilters] = useState({ valid: undefined, year: undefined });
  
  const [form] = Form.useForm();
  const { entrepots } = useFetchAllEntrepots();
  const { defaultCode } = useDefaultEntrepot();

  useEffect(() => {
    fetchInventaires();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchInventaires = async () => {
    try {
      setLoading(true);
      const response = await listInventaires({
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...filters
      });
      
      if (response.data.success) {
        setInventaires(response.data.items);
        setPagination(prev => ({
          ...prev,
          total: response.data.total
        }));
      }
    } catch (error) {
      message.error('Erreur lors du chargement des inventaires');
      console.error('Error fetching inventaires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleCreateInventaire = async (values) => {
    try {
      setCreating(true);
      const fallbackEntrepot = values.codeEnt || defaultCode || (await getDefaultEntrepot());
      const response = await createInventaire({
        codeEnt: fallbackEntrepot,
        year: values.year || new Date().getFullYear(),
        remarque: values.remarque,
        useCurrentStock: false // Create header only
      });
      
      if (response.data.success) {
        message.success('Inventaire créé avec succès');
        setCreateModalOpen(false);
        form.resetFields();
        fetchInventaires();
        // Navigate to the created inventaire
        if (response.data.data?.id) {
          onSelectInventaire(response.data.data.id);
        }
      }
    } catch (error) {
      message.error(`Erreur: ${error.response?.data?.error || error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const openCreateModal = () => {
    form.setFieldsValue({
      codeEnt: defaultCode || undefined,
      year: new Date().getFullYear(),
    });
    setCreateModalOpen(true);
  };

  const handleValidate = (id) => {
    Modal.confirm({
      title: 'Valider l\'inventaire',
      content: 'Êtes-vous sûr de vouloir valider cet inventaire? Les stocks seront mis à jour selon les quantités enregistrées.',
      okText: 'Oui, valider',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await validateInventaire(id);
          message.success('Inventaire validé avec succès');
          fetchInventaires();
        } catch (error) {
          message.error(`Erreur: ${error.response?.data?.error || error.message}`);
        }
      }
    });
  };

  const handleDelete = (id, codeInv) => {
    Modal.confirm({
      title: 'Supprimer l\'inventaire',
      content: `Êtes-vous sûr de vouloir supprimer l'inventaire ${codeInv}?`,
      okText: 'Oui, supprimer',
      cancelText: 'Annuler',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteInventaire(id);
          message.success('Inventaire supprimé avec succès');
          fetchInventaires();
        } catch (error) {
          message.error(`Erreur: ${error.response?.data?.error || error.message}`);
        }
      }
    });
  };

  const handleRecalcStock = () => {
    Modal.confirm({
      title: 'Recalculer les stocks',
      content: 'Cette opération va recalculer tous les stocks en se basant sur le dernier inventaire validé et toutes les transactions depuis. Continuer?',
      okText: 'Oui, recalculer',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          setRecalculating(true);
          const response = await recalcStockFromInventaire();
          if (response.data.success) {
            message.success(`Stocks recalculés avec succès. ${response.data.updated} articles mis à jour depuis l'inventaire ${response.data.inventaireCode}`);
          }
        } catch (error) {
          message.error(`Erreur: ${error.response?.data?.error || error.message}`);
        } finally {
          setRecalculating(false);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Code Inventaire',
      dataIndex: 'CODE_INV',
      key: 'CODE_INV',
      render: (text, record) => (
        <Space>
          <span style={{ fontWeight: 'bold' }}>{text}</span>
          {record.VALID ? (
            <Tag color="green">Validé</Tag>
          ) : (
            <Tag color="orange">Brouillon</Tag>
          )}
          {record.isActive && (
            <Tag color="blue">Actif</Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Entrepôt',
      key: 'entrepot',
      render: (_, record) => (
        <span>{record.entrepot?.code_ent} - {record.entrepot?.name}</span>
      )
    },
    {
      title: 'Année',
      dataIndex: 'YEAR',
      key: 'YEAR'
    },
    {
      title: 'Articles',
      key: 'articles',
      render: (_, record) => record._count?.details || 0
    },
    {
      title: 'Montant HT',
      dataIndex: 'MNT_HT',
      key: 'MNT_HT',
      render: (value) => `${value?.toFixed(2) || '0.00'} DH`
    },
    {
      title: 'Montant TTC',
      dataIndex: 'MNT_TTC',
      key: 'MNT_TTC',
      render: (value) => `${value?.toFixed(2) || '0.00'} DH`
    },
    {
      title: 'Date Création',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Date Validation',
      dataIndex: 'DATE_VALID',
      key: 'DATE_VALID',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => onSelectInventaire && onSelectInventaire(record.id)}
          >
            Voir
          </Button>
          {!record.VALID && (
            <>
              <PermissionButton permissionModule="inventory" permissionAction="validate"
                type="primary"
                icon={<CheckCircleOutlined />}
                size="small"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                onClick={() => handleValidate(record.id)}
              >
                Valider
              </PermissionButton>
              <PermissionButton permissionModule="inventory" permissionAction="delete"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => handleDelete(record.id, record.CODE_INV)}
              >
                Supprimer
              </PermissionButton>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Liste des Inventaires</h2>
          <Space>
            <PermissionButton permissionModule="inventory" permissionAction="recalculate"
              icon={<ReloadOutlined />}
              onClick={handleRecalcStock}
              loading={recalculating}
            >
              Recalculer Stocks
            </PermissionButton>
            <PermissionButton permissionModule="inventory" permissionAction="create"
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
            >
              Nouvel Inventaire
            </PermissionButton>
          </Space>
        </div>

        <Space style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 200 }}
            placeholder="Filtrer par statut"
            allowClear
            onChange={(value) => setFilters(prev => ({ ...prev, valid: value }))}
          >
            <Option value={true}>Validés</Option>
            <Option value={false}>Brouillons</Option>
          </Select>
          <Select
            style={{ width: 150 }}
            placeholder="Filtrer par année"
            allowClear
            onChange={(value) => setFilters(prev => ({ ...prev, year: value }))}
          >
            {[...Array(5)].map((_, i) => {
              const year = new Date().getFullYear() - i;
              return <Option key={year} value={year}>{year}</Option>;
            })}
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={inventaires}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          size="small"
        />
      </Card>

      <Modal
        title="Créer un Inventaire"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateInventaire}
          initialValues={{
            codeEnt: defaultCode || undefined,
            year: new Date().getFullYear(),
          }}
        >
          <Form.Item
            name="codeEnt"
            label="Entrepôt"
            rules={[{ required: true, message: 'Veuillez sélectionner un entrepôt' }]}
          >
            <Select placeholder="Sélectionner un entrepôt">
              {entrepots?.map(ent => (
                <Option key={ent.code_ent} value={ent.code_ent}>
                  {ent.code_ent} - {ent.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="year"
            label="Année"
            initialValue={new Date().getFullYear()}
          >
            <Select>
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return <Option key={year} value={year}>{year}</Option>;
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="remarque"
            label="Remarque"
          >
            <TextArea rows={3} placeholder="Notes sur l'inventaire..." />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <PermissionButton   onClick={() => {
                setCreateModalOpen(false);
                form.resetFields();
              }}>
                Annuler
              </PermissionButton>
              <PermissionButton permissionModule="inventory" permissionAction="create" type="primary" htmlType="submit" loading={creating}>
                Créer
              </PermissionButton>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InventaireList;

